/**
 * 5인 미만/5인 이상 사업장 판정 (월 말일 기준 재직 인원)
 * 설계문서: required/5인미만/5인이상.md §2
 */

import type { Employee } from '../types/employee';
import { Timestamp } from 'firebase/firestore';
import { useRatesStore } from '../store/ratesStore';

function toDate(value: Timestamp | Date | string | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (value && typeof (value as Timestamp).toDate === 'function') return (value as Timestamp).toDate();
  return null;
}

/**
 * 해당 월 말일 기준 재직 인원 수
 * - 가계부(accountNumber) 일치
 * - 퇴직자 제외 (isRetired !== true)
 * - 입사일 없으면 포함, 있으면 hireDate <= 해당 월 마지막 날일 때만 포함
 */
export function getActiveEmployeeCountForMonth(
  employees: Employee[],
  month: Date,
  accountNumber: number
): number {
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

  return employees.filter((e) => {
    const accountMatch =
      (e.account_number !== undefined && e.account_number === accountNumber) ||
      (e.accountNumber !== undefined && e.accountNumber === accountNumber);
    if (!accountMatch) return false;
    if (e.isRetired === true) return false;

    const hireDate = toDate(e.hire_date ?? e.hireDate);
    if (hireDate == null) return true;
    return hireDate <= lastDay;
  }).length;
}

/**
 * 5인 이상 사업장 여부 (해당 월 말일 기준 재직 인원 ≥ threshold).
 * threshold: Firestore SSOT (`labor.companySize.threshold`) 우선, 미동기화 시 5 fallback.
 */
export function calculateBusinessScale(
  employees: Employee[],
  month: Date,
  accountNumber: number
): boolean {
  const threshold =
    useRatesStore.getState().current.labor?.companySize?.threshold ?? 5;
  return getActiveEmployeeCountForMonth(employees, month, accountNumber) >= threshold;
}

/**
 * 노동절(5/1) 가산 적용을 위한 5인 이상 판정.
 * 설계문서 §7: **전월 말일** 기준으로 고정 (당월 인원 변동에 따른 들쭉날쭉 방지).
 * scale_override === 'under5' 일 경우 호출부에서 false 로 강제.
 */
export function calculate5PlusForLaborDay(
  employees: Employee[],
  targetMonth: Date,
  accountNumber: number
): boolean {
  const priorMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() - 1, 1);
  return calculateBusinessScale(employees, priorMonth, accountNumber);
}
